import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, Crosshair, Search, Home, Briefcase, Clock } from "lucide-react";
import { searchPlaces } from "../lib/location";

/**
 * AddressAutocomplete
 * ───────────────────
 * Uber-style live address search built on OpenStreetMap Nominatim.
 * As the user types, we debounce a search query (250ms) and render a
 * floating dropdown of matches. Tapping a suggestion resolves address +
 * coords in a single atomic update via `onSelect({address,lat,lng})`,
 * which lets the parent recompute the fare instantly.
 *
 * Props:
 *  - value      → current text in the input (parent-controlled)
 *  - onChange   → called with the raw typed string on every keystroke
 *  - onSelect   → called with {address,lat,lng} when the user picks
 *  - placeholder, testid, dotClassName, ...
 *  - showCurrentLocation → renders a "Use my location" chip at top (pickup only)
 *  - onUseCurrentLocation → callback for the current-location action
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  testid,
  dotClassName,
  leadingSlot,
  trailingSlot,
  showCurrentLocation = false,
  onUseCurrentLocation,
  savedPlaces = [],      // [{label:'Home',address,lat,lng}]
  recentPlaces = [],     // [{address,lat,lng}]
  hasCoords = false,
  autoFocus = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const lastQueryRef = useRef("");

  // Debounced live search — 250ms feels responsive without hammering Nominatim.
  useEffect(() => {
    if (!open) return;
    const q = (value || "").trim();
    if (q.length < 2) {
      setSuggestions([]);
      setBusy(false);
      return;
    }
    // Skip re-searching after user picked (address now === last query result)
    if (hasCoords && q === lastQueryRef.current) return;
    setBusy(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      const results = await searchPlaces(q);
      if (cancelled) return;
      setSuggestions(results.slice(0, 6));
      setBusy(false);
      setActiveIndex(-1);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [value, open, hasCoords]);

  // Close dropdown when clicking outside the wrapper
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  const pick = (s) => {
    lastQueryRef.current = s.label;
    onSelect({ address: s.label, lat: s.lat, lng: s.lng });
    setSuggestions([]);
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = activeIndex >= 0 ? activeIndex : 0;
      if (suggestions[idx]) pick(suggestions[idx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative flex-1">
      <div className="flex items-center gap-3">
        {leadingSlot || (dotClassName && <span className={`w-2.5 h-2.5 flex-shrink-0 ${dotClassName}`} />)}
        <input
          ref={inputRef}
          type="text"
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          className="flex-1 bg-transparent outline-none text-[15px] text-white placeholder:text-white/40 tracking-tight"
          data-testid={testid}
        />
        {busy && !hasCoords && (
          <Loader2 size={12} className="animate-spin text-white/40" data-testid={`${testid}-busy`} />
        )}
        {trailingSlot}
      </div>

      {open && (suggestions.length > 0 || showCurrentLocation || busy || savedPlaces.length > 0 || recentPlaces.length > 0) && (
        <div
          className="absolute z-50 left-0 right-0 top-full mt-2 bg-obsidian/95 border border-white/15 rounded-2xl backdrop-blur-md shadow-[0_16px_48px_-8px_rgba(0,0,0,0.9)] overflow-hidden max-h-[400px] overflow-y-auto"
          data-testid={`${testid}-dropdown`}
        >
          {showCurrentLocation && onUseCurrentLocation && (
            <button
              type="button"
              onClick={() => { onUseCurrentLocation(); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition text-start border-b border-white/5"
              data-testid={`${testid}-use-location`}
            >
              <div className="w-8 h-8 rounded-full bg-electric-teal/15 text-electric-teal flex items-center justify-center">
                <Crosshair size={14} strokeWidth={2.5} />
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-black text-electric-teal">Use my current location</div>
                <div className="text-[11px] text-white/50">GPS-accurate pickup</div>
              </div>
            </button>
          )}

          {/* Saved (Home / Work) + Recent — shown only when the user hasn't
              typed a search query yet, so real results still take priority. */}
          {(value || "").trim().length < 2 && (
            <>
              {savedPlaces.length > 0 && (
                <div>
                  <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest font-black text-white/40">Saved</div>
                  {savedPlaces.map((p, i) => {
                    const isHome = /home|home/i.test(p.label || "");
                    const Icon = isHome ? Home : Briefcase;
                    return (
                      <button
                        key={`saved-${i}`}
                        type="button"
                        onClick={() => pick({ label: p.address, lat: p.lat, lng: p.lng })}
                        className="w-full flex items-start gap-3 px-4 py-2.5 text-start hover:bg-white/5 transition"
                        data-testid={`${testid}-saved-${i}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-electric-teal/15 text-electric-teal flex items-center justify-center shrink-0 mt-0.5">
                          <Icon size={14} strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[14px] font-semibold text-white truncate">{p.label}</div>
                          <div className="text-[11px] text-white/50 truncate">{p.address}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {recentPlaces.length > 0 && (
                <div className={savedPlaces.length > 0 ? "border-t border-white/5" : ""}>
                  <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest font-black text-white/40">Recent</div>
                  {recentPlaces.slice(0, 4).map((p, i) => {
                    const primary = (p.address || "").split(",")[0];
                    const rest = (p.address || "").split(",").slice(1).join(",").trim();
                    return (
                      <button
                        key={`recent-${i}`}
                        type="button"
                        onClick={() => pick({ label: p.address, lat: p.lat, lng: p.lng })}
                        className="w-full flex items-start gap-3 px-4 py-2.5 text-start hover:bg-white/5 transition"
                        data-testid={`${testid}-recent-${i}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-white/5 text-white/60 flex items-center justify-center shrink-0 mt-0.5">
                          <Clock size={13} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[14px] font-semibold text-white truncate">{primary}</div>
                          {rest && <div className="text-[11px] text-white/50 truncate">{rest}</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
          {busy && (
            <div className="flex items-center gap-2 px-4 py-3 text-xs text-white/50">
              <Loader2 size={12} className="animate-spin" />
              Searching…
            </div>
          )}
          {!busy && suggestions.map((s, i) => {
            const primary = s.label.split(",")[0];
            const rest = s.label.split(",").slice(1).join(",").trim();
            return (
              <button
                key={`${s.lat}-${s.lng}-${i}`}
                type="button"
                onClick={() => pick(s)}
                onMouseEnter={() => setActiveIndex(i)}
                className={`w-full flex items-start gap-3 px-4 py-3 text-start transition ${
                  activeIndex === i ? "bg-white/10" : "hover:bg-white/5"
                }`}
                data-testid={`${testid}-suggestion-${i}`}
              >
                <div className="w-8 h-8 rounded-full bg-white/10 text-white/70 flex items-center justify-center shrink-0 mt-0.5">
                  <MapPin size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-white truncate">{primary}</div>
                  {rest && <div className="text-[11px] text-white/50 truncate">{rest}</div>}
                </div>
              </button>
            );
          })}
          {!busy && suggestions.length === 0 && (value || "").trim().length >= 2 && (
            <div className="flex items-center gap-2 px-4 py-3 text-xs text-white/50">
              <Search size={12} /> No matches — keep typing…
            </div>
          )}
        </div>
      )}
    </div>
  );
}
